import "./inventory_adjustments.css";
import React, { useCallback, useEffect, useState } from "react";

// components
import PageHeader from "../../../components/common/PageHeader/PageHeader";
import InputBox from "../../../components/common/InputBox/InputBox";
import AddButtonTable from "../../../components/common/AddButton/addButtonTable";
import AddButton from "../../../components/common/AddButton/AddButton";
import SearchSelectBox from "../../../components/common/SelectBox/SelectBoxWithSearch";
import {
  ActiveWarehousesWithLocations,
  getLocationQuantities,
  VariantsActive,
} from "../../../services/ProductApi";
import CloseRed from "../../../components/common/CloseButton/CloseRed";
import CloseButton from "../../../components/common/CloseButton/CloseButton2";
import { useNavigate } from "react-router-dom";
import { CreateInventoryAdjustment } from "../../../services/StockApi";
import { SuccessAlert } from "../../../components/common/ErrorAlert/SuccessAlert";
import ErrorNotification from "../../../components/common/ErrorAlert/ErrorNotification";
import DateTimeInputField from "../../../components/common/DateTimePicker/DateTimeInputField";
import { useExitWarning } from "../../../hooks/useExitWarning";
import ExitPageWarningPopup from "../../../components/popup/ExitPagePopup/ExitPageWarningPopup";
import { useTranslation } from "react-i18next";
import DiscountBox from "../../../components/common/InputAndSelect/DropDownRight";
import WithLoading from "../../../components/common/WithLoading/WithLoading";
import { usePageLoading } from "../../../store/useLoadingStore";
import useBatchTrackingStore from "../../../store/useBatchTrackingStore";
import NotesBox from "../../../components/common/InputBox/Notesbox";
import { getOrgNowISO } from "../../../utils/dates";

const AddInventoryAdjustments = () => {
  const [isLoading, setIsLoading] = usePageLoading();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [adjustment, setAdjustment] = useState({
    date: getOrgNowISO(),
    based_on: "uom",
    status: "draft",
    description: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [productOptions, setProductOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Options for warehouses and locations
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState({});
  const [locationQuantities, setLocationQuantities] = useState({});
  const isBatchTrackingEnabled = useBatchTrackingStore(
    (state) => state.isBatchTrackingEnabled,
  );
  const [isDirty, setIsDirty] = useState(false);
  const { showExitPopup, handleStay, handleLeave } = useExitWarning(isDirty);
  const navigate = useNavigate();

  // Adjustment type options based on your backend model
  const adjustmentTypeOptions = [
    // Minus Adjustments (reductions)
    { value: "damage", label: "Damage / Defective", type: "minus" },
    { value: "expired", label: "Expired / Obsolete", type: "minus" },
    { value: "lost", label: "Lost / Shrinkage", type: "minus" },
    { value: "consumption", label: "Internal Consumption", type: "minus" },
    { value: "return_vendor", label: "Return to Vendor", type: "minus" },

    // Plus Adjustments (increases)
    {
      value: "surplus",
      label: "Surplus (Stock Count Difference)",
      type: "plus",
    },
    { value: "customer_return", label: "Customer Return", type: "plus" },
    { value: "correction", label: "Data Entry Correction", type: "plus" },
  ];

  const BOTH_DIRECTION_TYPES = ["correction"];
  
  const getSignOptions = (adjustmentType) => {
    const typeInfo = adjustmentTypeOptions.find(opt => opt.value === adjustmentType);
    if (!typeInfo) return [{ label: "+", value: "plus" }];
    
    if (BOTH_DIRECTION_TYPES.includes(adjustmentType)) {
      // correction: user can choose + or -
      return [
        { label: "+", value: "plus" },
        { label: "-", value: "minus" },
      ];
    }
    
    // Fixed direction types: return only one option (no switching)
    return [{ label: typeInfo.type === "minus" ? "-" : "+", value: typeInfo.type }];
  };

  const fetchProducts = useCallback(async () => {
    try {
      const products = await VariantsActive();
      setProductOptions(products);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    }
  }, []);

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await ActiveWarehousesWithLocations();
      const warehouses = response?.data?.data || [];

      const formattedWarehouses = warehouses.map((warehouse) => ({
        value: warehouse.id,
        label:
          warehouse.name ||
          `${warehouse.warehouse_type} ${warehouse.vehicle?.license_plate || ""}`,
        warehouse_type: warehouse.warehouse_type,
        locations: warehouse.locations || [],
        raw: warehouse,
      }));

      setWarehouseOptions(formattedWarehouses);
    } catch (error) {
      console.error("Failed to fetch warehouses:", error);
    }
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchProducts(),
          fetchWarehouses(),
        ]);
      } catch (error) {
        console.error("Failed to load inventory adjustment options:", error);
      } finally {
        setIsLoading(false);
        setIsInitialLoad(false);
      }
    };
    initializeData();
  }, [fetchProducts, fetchWarehouses]);



  const calculateCurrentQuantity = (previousQty, adjustmentQty, adjustmentType, adjustmentDirection) => {
    const prev = parseFloat(previousQty) || 0;
    const adj = parseFloat(adjustmentQty) || 0;

    let isMinusAdjustment;

    if (BOTH_DIRECTION_TYPES.includes(adjustmentType)) {
      // For "correction", use the explicitly chosen direction
      isMinusAdjustment = adjustmentDirection === "minus";
    } else {
      const typeInfo = adjustmentTypeOptions.find(opt => opt.value === adjustmentType);
      isMinusAdjustment = typeInfo?.type === "minus";
    }

    return isMinusAdjustment ? Math.max(0, prev - adj) : prev + adj;
  };

  const handleLineChange = async (field, value, index) => {
    setIsDirty(true);
    const updatedLines = [...products];
    updatedLines[index][field] = value;

    // Clear field-specific errors when user makes changes
    if (fieldErrors[`${field}_${index}`]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`${field}_${index}`];
        return newErrors;
      });
    }

    // Handle warehouse selection - populate locations
    if (field === "warehouse") {
      // Clear warehouse error
      if (fieldErrors[`warehouse_${index}`]) {
        setFieldErrors((prev) => {
          const { [`warehouse_${index}`]: _, ...rest } = prev;
          return rest;
        });
      }

      const selectedWarehouse = warehouseOptions.find((w) => w.value === value);
      if (selectedWarehouse && selectedWarehouse.locations) {
        const formattedLocations = selectedWarehouse.locations.map(
          (location) => ({
            value: location.id,
            label: location.name,
            is_primary: location.is_primary,
            raw: location,
          }),
        );

        setLocationOptions((prev) => ({
          ...prev,
          [index]: formattedLocations,
        }));

        // Clear dependent fields when warehouse changes
        updatedLines[index].location = "";
        updatedLines[index].product = "";
        updatedLines[index].batch_id = "";
        updatedLines[index].previous_quantity = "";
        updatedLines[index].adjustment_quantity = "";
        updatedLines[index].current_quantity = "";

        // Clear dependent field errors
        setFieldErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[`location_${index}`];
          delete newErrors[`product_${index}`];
          delete newErrors[`batch_${index}`];
          delete newErrors[`adjustment_quantity_${index}`];
          return newErrors;
        });
      }
    }

    // Handle location selection - clear product and quantities
    if (field === "location") {
      // Clear location error
      if (fieldErrors[`location_${index}`]) {
        setFieldErrors((prev) => {
          const { [`location_${index}`]: _, ...rest } = prev;
          return rest;
        });
      }

      updatedLines[index].product = "";
      updatedLines[index].batch_id = "";
      updatedLines[index].previous_quantity = "";
      updatedLines[index].adjustment_quantity = "";
      updatedLines[index].current_quantity = "";

      // Clear dependent field errors
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`product_${index}`];
        delete newErrors[`batch_${index}`];
        delete newErrors[`adjustment_quantity_${index}`];
        return newErrors;
      });
    }

    // Handle product selection - fetch current stock
    if (field === "product") {
      // Clear product error
      if (fieldErrors[`product_${index}`]) {
        setFieldErrors((prev) => {
          const { [`product_${index}`]: _, ...rest } = prev;
          return rest;
        });
      }

      const warehouseId = updatedLines[index].warehouse;
      const locationId = updatedLines[index].location;

      if (warehouseId && locationId && value) {
        try {
          const response = await getLocationQuantities(value, warehouseId);
          const locationData = response?.data || [];

          // Find the specific location's quantity
          const specificLocationData = locationData.find(
            (loc) =>
              loc.location === locationId || loc.location_id === locationId,
          );

          const availableQty = parseFloat(specificLocationData?.available_quantity || 0);
          let finalAvailableQty = availableQty;
          let latestBatchId = null;

          if (isBatchTrackingEnabled) {
            const batches = specificLocationData?.batches || [];
            if (batches.length > 0) {
              const latestBatch = batches.reduce((prev, current) => (prev.id > current.id) ? prev : current, batches[0]);
              latestBatchId = latestBatch.id;
              finalAvailableQty = parseFloat(latestBatch.quantity || 0);
            }
            updatedLines[index].batch_id = latestBatchId;
          }

          updatedLines[index].previous_quantity = finalAvailableQty;

          // Update location quantities for this specific index
          setLocationQuantities((prev) => ({
            ...prev,
            [index]: {
              available_qty: finalAvailableQty,
              location_data: specificLocationData,
            },
          }));

          // Recalculate current quantity if adjustment quantity and type exist
          if (
            updatedLines[index].adjustment_quantity &&
            updatedLines[index].adjustment_type
          ) {
            updatedLines[index].current_quantity = calculateCurrentQuantity(
              finalAvailableQty,
              updatedLines[index].adjustment_quantity,
              updatedLines[index].adjustment_type,
              updatedLines[index].adjustment_direction
            );
          }
        } catch (error) {
          console.error("Failed to fetch location quantities:", error);
        }
      }
    }

    // Handle batch_id selection
    if (field === "batch_id") {
      const locationData = locationQuantities[index]?.location_data;
      const batches = locationData?.batches || [];
      const selectedBatch = batches.find((b) => b.id === parseInt(value, 10)) || null;
      const batchQty = selectedBatch ? parseFloat(selectedBatch.quantity || 0) : 0;

      updatedLines[index].batch_id = value ? parseInt(value, 10) : null;
      updatedLines[index].previous_quantity = batchQty;

      setLocationQuantities((prev) => ({
        ...prev,
        [index]: {
          ...prev[index],
          available_qty: batchQty,
        },
      }));

      // Recalculate current quantity if adjustment quantity and type exist
      const adjQty = updatedLines[index].adjustment_quantity;
      const adjType = updatedLines[index].adjustment_type;
      if (adjQty && adjType) {
        updatedLines[index].current_quantity = calculateCurrentQuantity(
          batchQty,
          adjQty,
          adjType,
          updatedLines[index].adjustment_direction
        );
      }

      // Re-validate adjustment quantity against batch stock
      if (adjQty && adjType) {
        const typeInfo = adjustmentTypeOptions.find((opt) => opt.value === adjType);
        const isMinusAdjustment = BOTH_DIRECTION_TYPES.includes(adjType)
          ? updatedLines[index].adjustment_direction === "minus"
          : typeInfo?.type === "minus";

        const requestedQty = parseFloat(adjQty) || 0;
        if (isMinusAdjustment && requestedQty > batchQty) {
          setFieldErrors((prev) => ({
            ...prev,
            [`adjustment_quantity_${index}`]: `Cannot reduce more than available batch stock (${batchQty})`,
          }));
        } else {
          setFieldErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[`adjustment_quantity_${index}`];
            return newErrors;
          });
        }
      }
    }

    // Handle adjustment type selection
    if (field === "adjustment_type") {
      const typeInfo = adjustmentTypeOptions.find(opt => opt.value === value);
      updatedLines[index].adjustment_direction = BOTH_DIRECTION_TYPES.includes(value)
        ? "plus"  // default to plus for correction
        : typeInfo?.type || "plus";
      // Clear adjustment type error
      if (fieldErrors[`adjustment_type_${index}`]) {
        setFieldErrors((prev) => {
          const { [`adjustment_type_${index}`]: _, ...rest } = prev;
          return rest;
        });
      }

      // Recalculate current quantity if adjustment quantity exists
      const prevQty = updatedLines[index].previous_quantity;
      const adjQty = updatedLines[index].adjustment_quantity;

      if (prevQty !== "" && adjQty && value) {
        updatedLines[index].current_quantity = calculateCurrentQuantity(
          prevQty,
          adjQty,
          value,
          updatedLines[index].adjustment_direction
        );
      }
    }

    // Handle adjustment direction change
    if (field === "adjustment_direction") {
      const prevQty = updatedLines[index].previous_quantity;
      const adjQty = updatedLines[index].adjustment_quantity;
      const adjType = updatedLines[index].adjustment_type;

      // Recalculate current quantity if required fields are present
      if (prevQty !== "" && adjQty && adjType) {
        updatedLines[index].current_quantity = calculateCurrentQuantity(
          prevQty,
          adjQty,
          adjType,
          value
        );
      }

      // Re-validate adjustment quantity against stock
      if (adjQty && adjType) {
        const typeInfo = adjustmentTypeOptions.find(
          (opt) => opt.value === adjType,
        );
        const isMinusAdjustment = BOTH_DIRECTION_TYPES.includes(adjType)
          ? value === "minus"
          : typeInfo?.type === "minus";
        const prevQuantity = parseFloat(prevQty) || 0;
        const adjustmentQuantity = parseFloat(adjQty) || 0;

        if (isMinusAdjustment && adjustmentQuantity > prevQuantity) {
          setFieldErrors((prev) => ({
            ...prev,
            [`adjustment_quantity_${index}`]: `Cannot reduce more than available stock (${prevQuantity})`,
          }));
        } else if (adjustmentQuantity > 0) {
          // Clear error if it's now valid
          setFieldErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[`adjustment_quantity_${index}`];
            return newErrors;
          });
        }
      }
    }

    // Handle adjustment quantity change - recalculate current quantity and validate
    if (field === "adjustment_quantity") {
      const prevQty = updatedLines[index].previous_quantity;
      const adjType = updatedLines[index].adjustment_type;

      // Clear previous quantity error
      if (fieldErrors[`adjustment_quantity_${index}`]) {
        setFieldErrors((prev) => {
          const { [`adjustment_quantity_${index}`]: _, ...rest } = prev;
          return rest;
        });
      }

      // Calculate current quantity if required fields are present
      if (prevQty !== "" && adjType) {
        updatedLines[index].current_quantity = calculateCurrentQuantity(
          prevQty,
          value,
          adjType,
          updatedLines[index].adjustment_direction
        );
      }

      // Validate quantity
      if (value && parseFloat(value) <= 0) {
        setFieldErrors((prev) => ({
          ...prev,
          [`adjustment_quantity_${index}`]:
            "Adjustment quantity must be greater than 0",
        }));
      } else if (value && adjType) {
        const typeInfo = adjustmentTypeOptions.find(
          (opt) => opt.value === adjType,
        );
        const isMinusAdjustment = BOTH_DIRECTION_TYPES.includes(adjType)
          ? updatedLines[index].adjustment_direction === "minus"
          : typeInfo?.type === "minus";
        const prevQuantity = parseFloat(prevQty) || 0;
        const adjustmentQuantity = parseFloat(value) || 0;

        if (isMinusAdjustment && adjustmentQuantity > prevQuantity) {
          setFieldErrors((prev) => ({
            ...prev,
            [`adjustment_quantity_${index}`]: `Cannot reduce more than available stock (${prevQuantity})`,
          }));
        }
      }
    }

    setProducts(updatedLines);
  };

  // Enhanced validation for adding new lines
  const handleAddLine = () => {
    setIsDirty(true);
    setFieldErrors({});

    const validateForm = () => {
      const errors = {};

      // Check if there's an existing incomplete line
      for (let index = 0; index < products.length; index++) {
        const product = products[index];

        if (!product.warehouse) {
          errors[`warehouse_${index}`] = t("Please select a warehouse.");
          break; // Stop at first incomplete line
        }
        if (!product.location) {
          errors[`location_${index}`] = t("Please select a location.");
          break;
        }
        if (!product.product) {
          errors[`product_${index}`] = t("Please select a product.");
          break;
        }
        if (!product.adjustment_type) {
          errors[`adjustment_type_${index}`] = t("Please select adjustment type.");
          break;
        }
        const typeInfo = adjustmentTypeOptions.find(
          (opt) => opt.value === product.adjustment_type,
        );
        const isMinusAdjustment = BOTH_DIRECTION_TYPES.includes(product.adjustment_type)
          ? product.adjustment_direction === "minus"
          : typeInfo?.type === "minus";

        if (isBatchTrackingEnabled && isMinusAdjustment && !product.batch_id) {
          errors[`batch_${index}`] = t("Please select a batch.");
          break;
        }
        if (
          !product.adjustment_quantity ||
          product.adjustment_quantity.toString().trim() === ""
        ) {
          errors[`adjustment_quantity_${index}`] = t("Please enter adjustment quantity.");
          break;
        }
      }

      return errors;
    };

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      return;
    }

    setProducts((prev) => [
      ...prev,
      {
        warehouse: "",
        location: "",
        product: "",
        batch_id: "",
        adjustment_type: "",
        adjustment_direction: "",
        previous_quantity: "",
        adjustment_quantity: "",
        current_quantity: "",
        reason: "",
      },
    ]);
  };

  // Comprehensive validation before submission
  const validateAdjustment = () => {
    const errors = {};

    // Check for adjustment date
    if (!adjustment.date) {
      errors.date = t("Please select adjustment date and time.");
    }

    // Check if at least one product is added
    if (!products || products.length === 0) {
      errors.form_error = t("Please add at least one product to adjust.");
      return errors;
    }

    // Validate all product lines
    for (let index = 0; index < products.length; index++) {
      const product = products[index];

      // Required field validations
      if (!product.warehouse) {
        errors[`warehouse_${index}`] = t("Please select a warehouse.");
      }
      if (!product.location) {
        errors[`location_${index}`] = t("Please select a location.");
      }
      if (!product.product) {
        errors[`product_${index}`] = t("Please select a product.");
      }
      if (!product.adjustment_type) {
        errors[`adjustment_type_${index}`] = t("Please select adjustment type.");
      } else {
        const typeInfo = adjustmentTypeOptions.find(
          (opt) => opt.value === product.adjustment_type,
        );
        const isMinusAdjustment = BOTH_DIRECTION_TYPES.includes(product.adjustment_type)
          ? product.adjustment_direction === "minus"
          : typeInfo?.type === "minus";

        if (isBatchTrackingEnabled && isMinusAdjustment && !product.batch_id) {
          errors[`batch_${index}`] = t("Please select a batch.");
        }
      }

      // Adjustment quantity validation
      if (
        !product.adjustment_quantity ||
        product.adjustment_quantity.toString().trim() === ""
      ) {
        errors[`adjustment_quantity_${index}`] = t("Please enter adjustment quantity.");
      } else {
        const qty = parseFloat(product.adjustment_quantity);
        const prevQty = parseFloat(product.previous_quantity) || 0;

        if (qty <= 0) {
          errors[`adjustment_quantity_${index}`] = t("Adjustment quantity must be greater than 0.");
        } else if (product.adjustment_type) {
          const typeInfo = adjustmentTypeOptions.find(
            (opt) => opt.value === product.adjustment_type,
          );
          const isMinusAdjustment = BOTH_DIRECTION_TYPES.includes(product.adjustment_type)
            ? product.adjustment_direction === "minus"
            : typeInfo?.type === "minus";

          if (isMinusAdjustment && qty > prevQty) {
            errors[`adjustment_quantity_${index}`] = t("Cannot reduce {{qty}} units when only {{prevQty}} available.", { qty: qty, prevQty: prevQty });
          }
        }
      }
    }

    return errors;
  };

  // Enhanced submit handler
  const handleCreateAdjustment = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setFieldErrors({});

    const validationErrors = validateAdjustment();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setIsSubmitting(false);
      return;
    }

    // Final check to prevent submission with invalid data
    for (let index = 0; index < products.length; index++) {
      const product = products[index];
      const adjustmentQty = parseFloat(product.adjustment_quantity);
      const previousQty = parseFloat(product.previous_quantity) || 0;

      if (product.adjustment_type) {
        const typeInfo = adjustmentTypeOptions.find(
          (opt) => opt.value === product.adjustment_type,
        );
        const isMinusAdjustment = BOTH_DIRECTION_TYPES.includes(product.adjustment_type)
          ? product.adjustment_direction === "minus"
          : typeInfo?.type === "minus";

        if (isMinusAdjustment && adjustmentQty > previousQty) {
          setFieldErrors({
            [`adjustment_quantity_${index}`]: t("Cannot reduce {{qty}} units when only {{prevQty}} available.", { qty: adjustmentQty, prevQty: previousQty }),
          });
          setIsSubmitting(false);
          return;
        }
      }
    }

    // Build adjustment payload
    const adjustmentPayload = {
      date: adjustment.date,
      based_on: adjustment.based_on,
      status: adjustment.status,
      number_of_products: products.length,
      description: adjustment.description || "",
    };

    // Build products payload
    const productsPayload = products.map((productLine) => ({
      warehouse: productLine.warehouse,
      warehouse_location: productLine.location,
      product: productLine.product,
      adjustment_type: productLine.adjustment_type,
      adjustment_direction: productLine.adjustment_direction,
      previous_quantity: parseFloat(productLine.previous_quantity) || 0,
      adjustment_quantity: parseFloat(productLine.adjustment_quantity) || 0,
      current_quantity: parseFloat(productLine.current_quantity) || 0,
      reason: productLine.reason || "",
      batch: isBatchTrackingEnabled ? productLine.batch_id || null : null,
    }));

    const payload = {
      adjustment: adjustmentPayload,
      products: productsPayload,
    };

    setIsLoading(true);
    try {
      const response = await CreateInventoryAdjustment(payload);
      const id = response.data?.adjustment_uuid;
      if (response?.data?.response_code === 1) {
        SuccessAlert(t("Inventory adjustment created successfully!"));
        navigate(`/inventory/stock/inventory-adjustments/details/${id}`);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  // Enhanced remove line with proper cleanup
  const handleRemoveLine = (index) => {
    setProducts((prev) => prev.filter((_, i) => i !== index));

    // Clean up location options and reindex
    setLocationOptions((prev) => {
      const copy = { ...prev };
      delete copy[index];
      const reIndexed = {};
      Object.keys(copy).forEach((key) => {
        const oldIndex = parseInt(key);
        if (oldIndex > index) {
          reIndexed[oldIndex - 1] = copy[key];
        } else if (oldIndex < index) {
          reIndexed[oldIndex] = copy[key];
        }
      });
      return reIndexed;
    });

    // Clean up location quantities and reindex
    setLocationQuantities((prev) => {
      const copy = { ...prev };
      delete copy[index];
      const reIndexed = {};
      Object.keys(copy).forEach((key) => {
        const oldIndex = parseInt(key);
        if (oldIndex > index) {
          reIndexed[oldIndex - 1] = copy[key];
        } else if (oldIndex < index) {
          reIndexed[oldIndex] = copy[key];
        }
      });
      return reIndexed;
    });

    // Clean up field errors and reindex
    setFieldErrors((prev) => {
      const newErrors = {};
      Object.keys(prev).forEach((key) => {
        if (key.includes("_")) {
          const parts = key.split("_");
          const errorIndex = parseInt(parts[parts.length - 1]);

          if (!isNaN(errorIndex)) {
            if (errorIndex < index) {
              newErrors[key] = prev[key];
            } else if (errorIndex > index) {
              const newKey = key.replace(
                `_${errorIndex}`,
                `_${errorIndex - 1}`,
              );
              newErrors[newKey] = prev[key];
            }
          } else {
            newErrors[key] = prev[key];
          }
        } else {
          newErrors[key] = prev[key];
        }
      });
      return newErrors;
    });
  };

  // Get filtered product options
  const getFilteredProductOptions = (currentIndex) => {
    const selectedProducts = products
      .filter((product, index) => index !== currentIndex && product.product)
      .map((product) => product.product);

    return productOptions.filter(
      (option) => !selectedProducts.includes(option.value),
    );
  };

  const handleCloseButtonClick = () => {
    navigate("/inventory/stock/inventory-adjustments");
  };

  if (isInitialLoad) return null;

  return (
    <div className="whole-container">
      <ExitPageWarningPopup
        isOpen={showExitPopup}
        onClose={handleStay}
        onSave={handleLeave}
      />
      <div className="path-header">
        <div className="path-name">
          <PageHeader
            breadcrumbs={[
              { label: "Stock" },
              {
                label: "Adjustments",
                path: "/inventory/stock/inventory-adjustments",
              },
              { label: "Create" },
            ]}
          />
        </div>
      </div>
      <div className="page-actions-bar">
        <div className="name-utility">
          <span className="title-heading">
            {t("Create Inventory Adjustment")}
          </span>
        </div>

        <div className="page-name-rightside-icons">
          <WithLoading>
            <AddButton
            isSubmitting={isSubmitting}
            onClick={handleCreateAdjustment}
            data-tooltip-id="global-tooltip"
            data-tooltip-content={t("Create Inventory Adjustment")}
          />
          </WithLoading>
          <CloseButton
            onClick={handleCloseButtonClick}
            data-tooltip-id="global-tooltip"
            data-tooltip-content={t("Close")}
          />
        </div>
      </div>
      <div style={{ marginTop: "1rem" }}>
        <ErrorNotification
          errors={fieldErrors}
          width="48%"
          marginBottom="0rem"
        />
      </div>
      <div className="adjustment-main-container">
        <div className="adjustment-section">
          <div className="adjustment-fields-card">
            <DateTimeInputField
              label="Adjustment Date & Time"
              defaultValue={adjustment.date}
              onChange={(e) =>
                setAdjustment((prev) => ({ ...prev, date: e.target.value }))
              }
              error={fieldErrors.date}
              important
              showTimeSelect={true}
            />
            <div style={{ marginTop: "1rem" }}>
              <NotesBox
                label="Description"
                defaultValue={adjustment.description}
                onChange={(e) =>
                  setAdjustment((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                className="w-100"
                useAddLabel
              />
            </div>
          </div>
        </div>
        <div>
          <table
            className="common-table no-hover-table"
            style={{ width: "100%", tableLayout: "fixed" }}
          >
            <thead className="common-table-head">
              <tr className="common-table-thead-tr">
                <th className={`tbl-start-pad ${isBatchTrackingEnabled ? "w-12" : "w-15"}`}>{t("Warehouse")}</th>
                <th className={isBatchTrackingEnabled ? "w-10" : "w-12"}>{t("Location")}</th>
                <th className="w-15">{t("Product")}</th>
                {isBatchTrackingEnabled && <th className="w-12">{t("Batch")}</th>}
                <th className="w-15">{t("Adjustment Type")}</th>
                <th className={isBatchTrackingEnabled ? "w-8" : "w-10"}>{t("On Hand Qty")}</th>
                <th className="w-8">{t("Adjustment Qty")}</th>
                <th className="w-8">{t("Current Qty")}</th>
                <th className={isBatchTrackingEnabled ? "w-10" : "w-12"}>{t("Notes")}</th>
                <th className="w-2"></th>
              </tr>
            </thead>

            <tbody className="compact-table-tbody common-table-tbody">
              {products.map((line, index) => {
                const batchesList = locationQuantities[index]?.location_data?.batches || [];
                const batchOptions = batchesList.map((b) => ({
                  value: b.id,
                  label: `${b.batch_name} (On Hand: ${b.quantity})`,
                }));

                return (
                  <tr key={index} style={{ verticalAlign: "top" }}>
                    {/* Warehouse */}
                    <td>
                      <SearchSelectBox
                        options={warehouseOptions}
                        defaultValue={line.warehouse}
                        onChange={(e) =>
                          handleLineChange("warehouse", e.target.value, index)
                        }
                        error={fieldErrors[`warehouse_${index}`]}
                      />
                    </td>

                    {/* Location */}
                    <td>
                      <SearchSelectBox
                        options={locationOptions[index] || []}
                        defaultValue={line.location}
                        onChange={(e) =>
                          handleLineChange("location", e.target.value, index)
                        }
                        disabled={!line.warehouse}
                        error={fieldErrors[`location_${index}`]}
                      />
                    </td>

                    {/* Product */}
                    <td>
                      <SearchSelectBox
                        className="w-100"
                        options={getFilteredProductOptions(index)}
                        defaultValue={line.product}
                        onChange={(e) =>
                          handleLineChange("product", e.target.value, index)
                        }
                        disabled={!line.location}
                        error={fieldErrors[`product_${index}`]}
                      />
                    </td>

                    {/* Batch - Only visible when batch tracking is enabled */}
                    {isBatchTrackingEnabled && (
                      <td>
                        <SearchSelectBox
                          className="w-100"
                          options={batchOptions}
                          defaultValue={line.batch_id}
                          onChange={(e) =>
                            handleLineChange("batch_id", e.target.value, index)
                          }
                          disabled={!line.product || batchOptions.length === 0}
                          placeholder={batchOptions.length === 0 ? "No batches" : "Select batch"}
                          error={fieldErrors[`batch_${index}`]}
                        />
                      </td>
                    )}

                    {/* Adjustment Type */}
                    <td>
                      <SearchSelectBox
                        className="w-100"
                        options={adjustmentTypeOptions}
                        defaultValue={line.adjustment_type}
                        onChange={(e) =>
                          handleLineChange( "adjustment_type",
                            e.target.value,
                            index,
                          )
                        }
                        error={fieldErrors[`adjustment_type_${index}`]}
                      />
                    </td>

                    {/* On Hand Qty - Read Only */}
                    <td>
                      <InputBox
                        className="w-100"
                        defaultValue={line.previous_quantity}
                        readOnly
                        disabled
                        placeholder={!line.product ? "Select product" : ""}
                        border={
                          line.previous_quantity !== "" && line.previous_quantity !== undefined && line.previous_quantity !== null
                            ? (parseFloat(line.previous_quantity) > 0 ? "green-border" : "red-border")
                            : ""
                        }
                        style={{
                          color:
                            line.previous_quantity !== "" && line.previous_quantity !== undefined && line.previous_quantity !== null
                              ? (parseFloat(line.previous_quantity) > 0 ? "#15803d" : "#dc2626")
                              : undefined,
                          fontWeight: "500",
                        }}
                      />
                    </td>

                    {/* Adjustment Qty */}
                    <td>
                      <DiscountBox
                        className="w-100"
                        defaultValue={line.adjustment_quantity}
                        options={getSignOptions(line.adjustment_type)}
                        defaultOption={
                          line.adjustment_direction ||
                          (adjustmentTypeOptions.find(o => o.value === line.adjustment_type)?.type === "minus"
                            ? "minus"
                            : "plus")
                        }
                        onChange={(e) =>
                          handleLineChange("adjustment_quantity", e.target.value, index)
                        }
                        onSelectChange={(e) =>
                          handleLineChange("adjustment_direction", e.target.value, index)
                        }
                        readOnly={!line.adjustment_type}
                        error={fieldErrors[`adjustment_quantity_${index}`]}
                      />
                    </td>

                    {/* Current Qty - Calculated */}
                    <td>
                      <InputBox
                        className="w-100"
                        defaultValue={line.current_quantity}
                        readOnly
                        border={
                          line.current_quantity !== "" && line.current_quantity !== undefined && line.current_quantity !== null
                            ? (parseFloat(line.current_quantity) > 0 ? "green-border" : "red-border")
                            : ""
                        }
                        style={{
                          color:
                            line.current_quantity !== "" && line.current_quantity !== undefined && line.current_quantity !== null
                              ? (parseFloat(line.current_quantity) > 0 ? "#15803d" : "#dc2626")
                              : undefined,
                          fontWeight: "500",
                        }}
                        placeholder={
                          !line.adjustment_quantity ? "Auto-calculated" : ""
                        }
                      />
                    </td>

                    {/* Notes */}
                    <td>
                      <NotesBox
                        label="Notes"
                        rows={2}
                        defaultValue={line.reason}
                        onChange={(e) =>
                          handleLineChange("reason", e.target.value, index)
                        }
                      />
                    </td>

                    {/* Remove */}
                    <td style={{ paddingTop: "0.5rem" }}>
                      <CloseRed
                        onClick={() => handleRemoveLine(index)}
                        data-tooltip-id="global-tooltip"
                        data-tooltip-content={t("Remove Line")}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div
            className="add-new-row"
            style={{ paddingLeft: "0.3rem", paddingTop: "0.5rem" }}
          >
            <WithLoading>
              <AddButtonTable
              imageSrc={"/assets/plus-yellow.png"}
              buttonText="Add New Row"
              onClick={handleAddLine}
            />
            </WithLoading>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddInventoryAdjustments;
